import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { submitTransaction, validateConsumer } from "@/lib/offline";
import { CYLINDERS, inr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Stepper } from "@/components/Stepper";
import { toast } from "sonner";
import { Search, CheckCircle2, XCircle } from "lucide-react";

export default function Deliver() {
  const navigate = useNavigate();
  const [cons, setCons] = useState("");
  const [info, setInfo] = useState(null);
  const [cyl, setCyl] = useState("DOM_14_2");
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState("cash");
  const [empties, setEmpties] = useState(1);
  const [prices, setPrices] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/prices/current").then((r) => {
      const m = {}; r.data.forEach((p) => (m[p.cylinder_type] = p.price)); setPrices(m);
    }).catch(() => {});
  }, []);

  const validate = async () => {
    if (!cons) return;
    const r = await validateConsumer(cons);
    setInfo(r);
    if (!r.valid) toast.error(r.offline ? "Not in offline list. Record as Non-consumer sale." : "Not found. Use Non-consumer sale instead.");
  };

  const amount = (prices[cyl] || 0) * qty;

  const submit = async () => {
    if (!info?.valid) { toast.error("Validate consumer number first"); return; }
    setBusy(true);
    try {
      const res = await submitTransaction({
        type: "consumer_delivery", consumer_no: cons, cylinder_type: cyl, qty,
        payment_mode: mode, empties_collected: empties,
      });
      toast.success(res.queued ? "Saved on phone — will sync when online" : "Delivery recorded");
      navigate("/driver");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 fade-up">
      <h1 className="font-head text-xl font-bold">Deliver to consumer</h1>
      <Card className="p-4 space-y-3">
        <Label>Consumer number</Label>
        <div className="flex gap-2">
          <Input inputMode="numeric" value={cons} onChange={(e) => { setCons(e.target.value.replace(/\D/g, "")); setInfo(null); }} className="font-mono h-12 text-base" placeholder="e.g. 600734" data-testid="driver-deliver-consumer-number-input" />
          <Button variant="outline" className="h-12" onClick={validate} data-testid="driver-deliver-validate-button"><Search className="h-5 w-5" /></Button>
        </div>
        {info && (info.valid ? (
          <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
            <CheckCircle2 className="h-4 w-4 mt-0.5" /><div><div className="font-medium">{info.consumer.name}</div><div className="text-xs">{info.consumer.area} · {info.consumer.package_desc}</div></div>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800"><XCircle className="h-4 w-4" /> Consumer not found</div>
        ))}
      </Card>

      <Card className="p-4 space-y-3">
        <Label>Cylinder type</Label>
        <div className="grid grid-cols-2 gap-2">
          {CYLINDERS.map((c) => (
            <button key={c.code} onClick={() => setCyl(c.code)} data-testid={`deliver-cyl-${c.code}`}
              className={`rounded-xl border p-3 text-sm font-medium ${cyl === c.code ? "border-primary bg-primary/10 text-primary" : "bg-card"}`}>{c.label}</button>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2"><Label>Quantity</Label><Stepper value={qty} onChange={(v) => setQty(Math.max(1, v))} min={1} testid="deliver-qty" /></div>
        <div className="flex items-center justify-between"><Label>Empties collected</Label><Stepper value={empties} onChange={setEmpties} testid="deliver-empties" /></div>
      </Card>

      <Card className="p-4 space-y-3">
        <Label>Payment</Label>
        <div className="grid grid-cols-2 gap-2">
          {["cash", "upi"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`deliver-mode-${m}`}
              className={`rounded-xl border p-3 font-medium uppercase ${mode === m ? "border-primary bg-primary/10 text-primary" : "bg-card"}`}>{m}</button>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-lg bg-secondary p-3"><span className="text-sm text-muted-foreground">Amount (fixed price)</span><span className="text-lg font-semibold">{inr(amount)}</span></div>
      </Card>

      <div className="fixed bottom-0 inset-x-0 max-w-md mx-auto p-4 bg-background/95 backdrop-blur border-t">
        <Button className="w-full h-14 text-base" onClick={submit} disabled={busy} data-testid="driver-deliver-submit-button">Record delivery</Button>
      </div>
    </div>
  );
}
